import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { webinarAPI } from '@/lib/api';
import { toast } from 'sonner';
import { ArrowLeft, Users, Clock, MessageSquare, BarChart3, Download, PlayCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';
import { AppLayout } from '@/components/layout/AppLayout';

export default function WebinarAnalyticsPage() {
  const { webinarId } = useParams();
  const navigate = useNavigate();
  const [webinar, setWebinar] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [webinarRes, analyticsRes] = await Promise.all([
          webinarAPI.get(webinarId),
          webinarAPI.getAnalytics(webinarId)
        ]);
        setWebinar(webinarRes.data);
        setAnalytics(analyticsRes.data);
      } catch {
        toast.error('Erreur lors du chargement des statistiques');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [webinarId]);

  if (loading) {
    return <div className="h-screen bg-zinc-950 flex items-center justify-center text-zinc-400">Chargement...</div>;
  }

  if (!webinar || !analytics) return null;

  const mockTimelineData = [
    { time: '0m', participants: 0 },
    { time: '5m', participants: Math.floor(analytics.peak_participants * 0.4) },
    { time: '15m', participants: Math.floor(analytics.peak_participants * 0.8) },
    { time: '30m', participants: analytics.peak_participants },
    { time: '45m', participants: Math.floor(analytics.peak_participants * 0.9) },
    { time: '60m', participants: Math.floor(analytics.peak_participants * 0.6) },
  ];

  const engagementData = [
    { name: 'Messages', value: analytics.total_messages },
    { name: 'Questions', value: analytics.total_questions },
    { name: 'Réactions', value: analytics.total_reactions },
  ];

  const formatDuration = (sec) => {
    const m = Math.floor(sec / 60);
    return m > 0 ? `${m} min` : `${sec} sec`;
  };

  const attendanceRate = analytics.total_registrations > 0 
    ? Math.round((analytics.total_attended / analytics.total_registrations) * 100) 
    : 0;

  return (
    <AppLayout>
    <div className="min-h-screen bg-zinc-950 text-white pb-12">
      {/* Header */}
      <div className="border-b border-zinc-800 bg-zinc-900/80 backdrop-blur sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate(`/admin/webinars`)}
              className="text-zinc-400 hover:text-white">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-lg font-bold flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-cyan-500" />
                Analytiques : {webinar.title}
              </h1>
              <p className="text-xs text-zinc-500">
                Diffusé le {new Date(webinar.schedule.start_at).toLocaleDateString('fr-FR')}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {webinar.recording?.status === 'ready' && (
              <Button variant="outline" className="gap-2 border-zinc-700 text-zinc-300 hover:text-white">
                <PlayCircle className="w-4 h-4" /> Voir le Replay
              </Button>
            )}
            <Button className="bg-cyan-600 hover:bg-cyan-700 text-white gap-2">
              <Download className="w-4 h-4" /> Exporter le rapport
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8 space-y-6">
        {/* KPIs */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-zinc-400">Inscrits vs Présents</CardTitle>
              <Users className="w-4 h-4 text-cyan-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-white">{analytics.total_attended} / {analytics.total_registrations}</div>
              <p className="text-xs text-emerald-400 mt-1">
                Taux de présence : {attendanceRate}%
              </p>
            </CardContent>
          </Card>
          
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-zinc-400">Pic de connexion</CardTitle>
              <Users className="w-4 h-4 text-violet-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-white">{analytics.peak_participants}</div>
              <p className="text-xs text-zinc-500 mt-1">Participants simultanés</p>
            </CardContent>
          </Card>

          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-zinc-400">Temps moyen passé</CardTitle>
              <Clock className="w-4 h-4 text-amber-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-white">{formatDuration(analytics.avg_watch_time_seconds)}</div>
              <p className="text-xs text-zinc-500 mt-1">Par participant</p>
            </CardContent>
          </Card>

          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-zinc-400">Interactions</CardTitle>
              <MessageSquare className="w-4 h-4 text-rose-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-white">
                {analytics.total_messages + analytics.total_reactions + analytics.total_questions}
              </div>
              <p className="text-xs text-zinc-500 mt-1">Messages et réactions combinés</p>
            </CardContent>
          </Card>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-base text-zinc-200">Rétention de l'audience</CardTitle>
            </CardHeader>
            <CardContent className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={mockTimelineData}>
                  <defs>
                    <linearGradient id="colorUsers" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#06b6d4" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272a" vertical={false} />
                  <XAxis dataKey="time" stroke="#52525b" fontSize={12} tickLine={false} axisLine={false} />
                  <YAxis stroke="#52525b" fontSize={12} tickLine={false} axisLine={false} />
                  <RechartsTooltip 
                    contentStyle={{ backgroundColor: '#18181b', border: '1px solid #27272a', borderRadius: '8px' }}
                    itemStyle={{ color: '#06b6d4' }}
                  />
                  <Area type="monotone" dataKey="participants" stroke="#06b6d4" strokeWidth={3} fillOpacity={1} fill="url(#colorUsers)" />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-base text-zinc-200">Engagement par type</CardTitle>
            </CardHeader>
            <CardContent className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={engagementData} layout="vertical" margin={{ top: 0, right: 30, left: 20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272a" horizontal={false} />
                  <XAxis type="number" stroke="#52525b" fontSize={12} tickLine={false} axisLine={false} />
                  <YAxis dataKey="name" type="category" stroke="#a1a1aa" fontSize={12} tickLine={false} axisLine={false} />
                  <RechartsTooltip 
                    cursor={{ fill: '#27272a' }}
                    contentStyle={{ backgroundColor: '#18181b', border: '1px solid #27272a', borderRadius: '8px' }}
                  />
                  <Bar dataKey="value" fill="#8b5cf6" radius={[0, 4, 4, 0]} barSize={30} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
    </AppLayout>
  );
}
